/**
 * Right-column tab data loaders (Insights + Public activity) extracted verbatim
 * from the tab-switch onClick in `ContactEditModal.tsx`.
 *
 * The orchestrator keeps the tab `onClick` shell (which still owns the
 * `setRightTab(tab)` call + the `tab === ... && ...` guards) and delegates the
 * fetch flows here. Behavior is unchanged (#3293 Pass-2): same endpoints, same
 * request bodies, same loading-flag sequencing, same swallow-on-error defaults.
 */

import { env } from '@/lib/env';
import { getAuthItem } from '@/lib/authStorage';
import { stripHtmlToText } from '../utils';

interface GmailMessageLike {
  date: string;
  from: string;
  body: string;
}

type EmailInsights = {
  tone?: string; dynamic?: string; topics?: string[];
  trajectory?: string; openLoops?: string[]; summary?: string;
} | null;

type PublicActivity = Array<{ date?: string; text: string; sourceUrl?: string }> | null;

function jsonHeaders(): Record<string, string> {
  const token = getAuthItem('xavior_auth_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export interface EmailInsightsDeps {
  gmailMessages: GmailMessageLike[];
  contactName: string;
  userName: string | undefined;
  setEmailInsightsLoading: (b: boolean) => void;
  setEmailInsights: (v: EmailInsights) => void;
}

/** Body of the `tab === 'insights'` branch — gated by the caller. */
export function loadEmailInsights(deps: EmailInsightsDeps): void {
  const { gmailMessages, contactName, userName, setEmailInsightsLoading, setEmailInsights } = deps;
  setEmailInsightsLoading(true);
  const headers = jsonHeaders();
  const emailData = gmailMessages
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .map(m => ({ date: m.date, from: m.from, body: stripHtmlToText(m.body).slice(0, 500) }));
  fetch(`${env.apiBaseUrl}/contacts/email-insights`, {
    method: 'POST', headers,
    body: JSON.stringify({ contactName, userName, emails: emailData }),
  })
    .then(r => r.ok ? r.json() : { insights: {} })
    .then(data => setEmailInsights(data.insights || {}))
    .catch(() => setEmailInsights({}))
    .finally(() => setEmailInsightsLoading(false));
}

export interface PublicActivityDeps {
  contactName: string;
  xHandle: string;
  company: string;
  firm: string;
  contactTitle: string;
  enrichmentSummary: string;
  setPublicActivityLoading: (b: boolean) => void;
  setPublicActivity: (v: PublicActivity) => void;
}

/** Body of the `tab === 'public'` branch — gated by the caller. */
export function loadPublicActivity(deps: PublicActivityDeps): void {
  const { contactName, xHandle, company, firm, contactTitle, enrichmentSummary, setPublicActivityLoading, setPublicActivity } = deps;
  setPublicActivityLoading(true);
  if (contactName.length >= 2) {
    const headers = jsonHeaders();
    fetch(`${env.apiBaseUrl}/contacts/recent-activity`, {
      method: 'POST', headers,
      body: JSON.stringify({ name: contactName, twitterHandle: xHandle || undefined, company: company || firm || undefined, title: contactTitle || undefined, bio: enrichmentSummary || undefined }),
    })
      .then(r => r.ok ? r.json() : { bullets: [] })
      .then(data => setPublicActivity(data.bullets || []))
      .catch(() => setPublicActivity([]))
      .finally(() => setPublicActivityLoading(false));
  } else {
    setPublicActivity([]);
    setPublicActivityLoading(false);
  }
}
